/**
 * Y3 Smiles Dental — Conversion Tracking Library
 * Fires events to GTM dataLayer (primary), GA4/Google Ads (if configured), and Meta Pixel.
 * All IDs are sourced from src/data/tracking.json or GATSBY_* env vars.
 * Rule: never hard-code IDs here. Never.
 */

// Runtime env IDs take precedence over the JSON file
const ENV_GA_TAG   = (typeof process !== 'undefined') && process.env?.GATSBY_GA_TAG_ID;
const ENV_ADS_ID   = (typeof process !== 'undefined') && process.env?.GATSBY_GOOGLE_ADS_ID;
const ENV_PIXEL_ID = (typeof process !== 'undefined') && process.env?.GATSBY_META_PIXEL_ID;

let _cfg = null;
function cfg() {
  if (_cfg) return _cfg;
  try {
    // The JSON is bundled by webpack at build time
    _cfg = require('../data/tracking.json');
  } catch (_) {
    _cfg = { google_tag_id: '', google_ads_id: '', conversion_labels: {}, meta_pixel_id: '' };
  }
  return _cfg;
}

function gaTagId()   { return ENV_GA_TAG  || cfg().google_tag_id  || ''; }
function adsId()     { return ENV_ADS_ID  || cfg().google_ads_id  || ''; }
function pixelId()   { return ENV_PIXEL_ID || cfg().meta_pixel_id || ''; }
function convLabel(key) { return (cfg().conversion_labels || {})[key] || ''; }

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function sha256(value) {
  if (typeof crypto === 'undefined' || !crypto.subtle) return null;
  const enc = new TextEncoder().encode(String(value).trim().toLowerCase());
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

function pushDL(obj) {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(obj);
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Fire a lead conversion after successful form submission.
 * Hashes email + phone for Enhanced Conversions.
 */
export async function fireLead({ service, email, phone, page }) {
  if (typeof window === 'undefined') return;
  const eventId = uuid();

  // GTM dataLayer — GTM handles GA4 + Google Ads triggers
  pushDL({
    event: 'booking_form_submit',
    service: service || 'general',
    page: page || window.location.pathname,
    event_id: eventId,
  });

  // Direct Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'Lead',
      { content_name: service || 'general', content_category: 'dental_booking', value: 0, currency: 'AUD' },
      { eventID: eventId }
    );
  }

  // Direct GA4 + Google Ads (if IDs configured outside GTM)
  if (window.gtag) {
    const [emailHash, phoneHash] = await Promise.all([
      email ? sha256(email) : null,
      phone ? sha256(phone.replace(/\s+/g, '')) : null,
    ]);
    if (emailHash || phoneHash) {
      window.gtag('set', 'user_data', {
        sha256_email_address: emailHash || undefined,
        sha256_phone_number: phoneHash || undefined,
      });
    }
    const ads = adsId();
    const label = convLabel('form_submit');
    if (ads && label) {
      window.gtag('event', 'conversion', {
        send_to: `${ads}/${label}`,
        value: 0, currency: 'AUD', transaction_id: eventId,
      });
    }
    const ga = gaTagId();
    if (ga) {
      window.gtag('event', 'generate_lead', { currency: 'AUD', service, page: window.location.pathname });
    }
  }

  return eventId;
}

/**
 * Fire a phone click conversion.
 * @param {string} location - identifies which page section fired the click
 */
export function firePhoneClick(location = 'unspecified') {
  if (typeof window === 'undefined') return;
  const eventId = uuid();

  pushDL({ event: 'phone_click', location, event_id: eventId });

  if (window.fbq) {
    window.fbq('track', 'Contact',
      { content_name: 'phone_click', content_category: 'dental_booking' },
      { eventID: eventId }
    );
  }
  if (window.gtag) {
    const ads = adsId();
    const label = convLabel('phone_click');
    if (ads && label) {
      window.gtag('event', 'conversion', { send_to: `${ads}/${label}`, transaction_id: eventId });
    }
    window.gtag('event', 'phone_click', { location, page: window.location.pathname });
  }
}

/**
 * Fire a directions (Google Maps) click. Soft conversion — GA4 + Meta only.
 * @param {string} location
 */
export function fireDirectionsClick(location = 'unspecified') {
  if (typeof window === 'undefined') return;
  const eventId = uuid();

  pushDL({ event: 'directions_click', location, event_id: eventId });

  if (window.fbq) {
    window.fbq('track', 'FindLocation',
      { content_name: 'directions_click', content_category: 'dental_booking' },
      { eventID: eventId }
    );
  }
  if (window.gtag) {
    window.gtag('event', 'directions_click', { location, page: window.location.pathname });
  }
}

/**
 * Fire an email click conversion.
 */
export function fireEmailClick() {
  if (typeof window === 'undefined') return;
  const eventId = uuid();

  pushDL({ event: 'email_click', event_id: eventId });

  if (window.fbq) {
    window.fbq('track', 'Contact',
      { content_name: 'email_click', content_category: 'dental_booking' },
      { eventID: eventId }
    );
  }
  if (window.gtag) {
    const ads = adsId();
    const label = convLabel('email_click');
    if (ads && label) {
      window.gtag('event', 'conversion', { send_to: `${ads}/${label}`, transaction_id: eventId });
    }
    window.gtag('event', 'email_click', { page: window.location.pathname });
  }
}

/**
 * Fire a booking intent signal when a CTA is clicked.
 *
 * Meta Pixel mapping note:
 *   We deliberately use `trackCustom('BookingIntent', ...)` here, NOT the
 *   standard `Schedule` event. The Meta Schedule event must represent an
 *   actual completed booking (so it can be optimized for and used as a
 *   conversion / lookalike seed). Pressing a "Book now" CTA is *intent*,
 *   not completion. The real Schedule event fires from a GTM Page View
 *   tag when the user lands on /booking-success/.
 *
 *   `trackCustom('BookingIntent', ...)` still produces a fully usable
 *   retargeting audience signal in Ads Manager (Custom Audience > Website
 *   > Custom Event = BookingIntent) without polluting the Schedule
 *   conversion volume.
 *
 * @param {string} ctaLocation
 */
export function fireBookingIntent(ctaLocation = 'unspecified') {
  if (typeof window === 'undefined') return;
  const eventId = uuid();

  pushDL({ event: 'booking_intent', cta_location: ctaLocation, event_id: eventId });

  if (window.fbq) {
    window.fbq('trackCustom', 'BookingIntent',
      { content_name: ctaLocation, content_category: 'dental_booking' },
      { eventID: eventId }
    );
  }
  if (window.gtag) {
    window.gtag('event', 'booking_intent', { cta_location: ctaLocation, page: window.location.pathname });
  }
}
